import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DataTable from "../components/common/DataTable";
import { erpApi, formatCurrency } from "../services/erpApi";

const categories = ["consultation", "admin", "lab", "dental", "diagnostic", "procedure", "pharmacy", "package"];
const emptyForm = {
  name: "",
  category: "consultation",
  description: "",
  is_active: true,
  pricing: [{ provider: "Clinic", cost_price: "", selling_price: "" }],
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const response = await erpApi.services({ active: null, limit: 500 });
      setServices(response.data);
    } catch {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = services.filter((item) => {
    const term = search.toLowerCase();
    return item.name.toLowerCase().includes(term) || item.category.toLowerCase().includes(term);
  });

  const setPricing = (index, key, value) => {
    setForm({
      ...form,
      pricing: form.pricing.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)),
    });
  };

  const addPricing = () => {
    setForm({ ...form, pricing: [...form.pricing, { provider: "", cost_price: "", selling_price: "" }] });
  };

  const removePricing = (index) => {
    setForm({ ...form, pricing: form.pricing.filter((_, rowIndex) => rowIndex !== index) });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (service) => {
    setEditing(service);
    setForm({
      name: service.name || "",
      category: service.category || "consultation",
      description: service.description || "",
      is_active: service.is_active !== false,
      pricing: (service.pricing || []).map((row) => ({
        provider: row.provider || "",
        cost_price: String(row.cost_price || ""),
        selling_price: String(row.selling_price || ""),
      })),
    });
    setOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.pricing.length || !form.pricing[0].selling_price) {
      toast.error("Service name and selling price are required");
      return;
    }
    const payload = {
      ...form,
      pricing: form.pricing.map((row) => ({
        provider: row.provider || "Clinic",
        cost_price: Number(row.cost_price || 0),
        selling_price: Number(row.selling_price || 0),
      })),
    };
    try {
      if (editing) {
        await erpApi.updateService(editing.id, payload);
        toast.success("Service updated");
      } else {
        await erpApi.createService(payload);
        toast.success("Service added");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service");
    }
  };

  const deactivate = async (service) => {
    if (!window.confirm(`Deactivate "${service.name}" from billing list?`)) return;
    await erpApi.deleteService(service.id);
    toast.success("Service deactivated");
    load();
  };

  const bootstrap = async () => {
    const response = await erpApi.bootstrapServices();
    toast.success(response.data.created ? "Starter services added" : "Starter services already exist");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-manrope text-2xl font-bold">Services & Pricing</h1>
          <p className="text-sm text-hospital-text-muted">Billing items, tests, packages and provider-wise prices.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={bootstrap}><RotateCcw className="mr-2 h-4 w-4" />Starter List</Button>
          <Button className="btn-primary" onClick={openCreate}><Plus className="mr-2 h-4 w-4" />Add Service</Button>
        </div>
      </div>

      <Card className="border border-gray-200">
        <CardContent className="p-4">
          <Input placeholder="Search service, test or category" value={search} onChange={(e) => setSearch(e.target.value)} />
        </CardContent>
      </Card>

      {loading ? (
        <div className="h-64 animate-pulse rounded-lg bg-white" />
      ) : (
        <DataTable
          title="Service Catalog"
          rows={filtered}
          empty="No services yet"
          columns={[
            { key: "name", label: "Service" },
            { key: "category", label: "Category", render: (row) => <span className="capitalize">{row.category}</span> },
            {
              key: "pricing",
              label: "Pricing",
              render: (row) => (
                <div className="space-y-1">
                  {(row.pricing || []).map((price) => (
                    <div key={`${row.id}-${price.provider}`} className="text-sm">
                      {price.provider}: <span className="font-semibold">{formatCurrency(price.selling_price)}</span>
                    </div>
                  ))}
                </div>
              ),
            },
            { key: "is_active", label: "Status", render: (row) => (row.is_active ? "Active" : "Inactive") },
            {
              key: "actions",
              label: "Actions",
              render: (row) => (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(row)}><Pencil className="h-4 w-4 text-hospital-primary" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => deactivate(row)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              ),
            },
          ]}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-3xl flex-col overflow-hidden p-0">
          <DialogHeader className="border-b border-gray-100 px-5 py-4">
            <DialogTitle>{editing ? "Edit Service" : "Add Service"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto px-5 py-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Service Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="space-y-3 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label>Provider Pricing</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addPricing}>Add Provider</Button>
                </div>
                {form.pricing.map((row, index) => (
                  <div key={index} className="grid grid-cols-1 gap-2 rounded-lg border border-gray-100 p-3 sm:grid-cols-[1fr_120px_120px_auto]">
                    <Input placeholder="Provider / Lab" value={row.provider} onChange={(e) => setPricing(index, "provider", e.target.value)} />
                    <Input type="number" placeholder="Cost" value={row.cost_price} onChange={(e) => setPricing(index, "cost_price", e.target.value)} />
                    <Input type="number" placeholder="Selling" value={row.selling_price} onChange={(e) => setPricing(index, "selling_price", e.target.value)} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePricing(index)} disabled={form.pricing.length === 1}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="btn-primary">{editing ? "Update Service" : "Save Service"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
